import React from "react";
import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import type { Theme } from "@material-ui/core/styles";
import { makeStyles } from "@material-ui/core/styles";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import clsx from "clsx";

import { Close as CloseIcon } from "@eroad/icons/material";
import { Marker } from "@eroad/icons/svg";

const useStyles = makeStyles(() => ({
  root: {
    maxHeight: "550px",
  },
  subtitle: {
    fontSize: "12px",
    fontWeight: "bold" as const,
    textTransform: "uppercase" as const,
    color: "rgba(0, 0, 0, 0.54)",
  },
  switchLabel: {
    flexGrow: 1,
  },
  markerGroup: {
    display: "flex",
    flexWrap: "wrap",
    "& > button": {
      border: "1px solid transparent!important",
      borderRadius: "30px",
    },
  },
  mt: {
    marginTop: "20px",
  },
  markerButton: {
    marginLeft: "0px!important",
    borderLeft: "1px solid #transparent!important",
    borderBottomLeftRadius: "30px!important",
    borderBottomRightRadius: "30px!important",
    borderTopLeftRadius: "30px!important",
    borderTopRightRadius: "30px!important",
  },
  header: {
    top: 0,
    "&:after": {
      borderBottom: "1px solid #DDD",
    },
  },
  footer: {
    bottom: 0,
  },
  close: {
    position: "absolute",
    right: "5px",
    top: "5px",
  },
  content: {
    maxHeight: "350px",
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: "8px",
      backgroundColor: "#F5F5F5",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "4px",
      backgroundColor: "#FFFFFF",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#BBBBBB",
      borderRadius: "4px",
    },
  },
}));

interface EditMarkerProps {
  name?: string;
  description?: string;
  notes?: string;
  isPrivate?: boolean;
  expires?: boolean;
  showDate?: Date;
  hideDate?: Date;
  markerColour?: string;
  onSave?: (markerDetails: Partial<EditMarkerProps>) => void;
  onClose?: () => void;
}

const EditMarker = ({
  name,
  description,
  notes,
  isPrivate,
  expires,
  showDate,
  hideDate,
  markerColour,
  onSave,
  onClose,
}: EditMarkerProps): JSX.Element => {
  const theme = useTheme();
  const classes = useStyles();
  const sm = useMediaQuery<Theme>(theme.breakpoints.down("sm"));
  const [markerName, setMarkerName] = React.useState<string>(name ?? "");
  const [markerNameValid, setMarkerNameValid] = React.useState<boolean>(true);
  const [markerNameValidation, setMarkerNameValidation] = React.useState<string>("");
  const [markerDescription, setMarkerDescription] = React.useState<string>(description ?? "");
  const [markerNotes, setMarkerNotes] = React.useState<string>(notes ?? "");
  const [isPrivateMarker, setIsPrivateMarker] = React.useState<boolean>(isPrivate ?? false);
  React.useEffect(() => {
    if (isPrivate !== undefined) setIsPrivateMarker(isPrivate);
  }, [isPrivate]);
  const [markerExpires, setMarkerExpires] = React.useState<boolean>(expires ?? false);
  React.useEffect(() => {
    if (expires !== undefined) setMarkerExpires(expires);
  }, [expires]);
  const [fromDate, setFromDate] = React.useState<Date>(showDate ?? new Date());
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const [toDate, setToDate] = React.useState<Date>(hideDate ?? tomorrow);
  React.useEffect(() => {
    if (showDate !== undefined) setFromDate(showDate);
  }, [showDate]);
  React.useEffect(() => {
    if (hideDate !== undefined) setToDate(hideDate);
  }, [hideDate]);

  const markerColours: string[] = [
    "#9A9A9A",
    "#409F2C",
    "#1B74D1",
    "#CC4AD7",
    "#AE262A",
    "#E75E1F",
    "#F7F32D",
    "#C0934F",
    "#000000",
    "#FFFFFF",
  ];
  const [selectedIndex, setSelectedIndex] = React.useState<string>(markerColour ?? "#9A9A9A");
  const onSelectMarker = (_event: React.MouseEvent<HTMLElement>, index: string) => setSelectedIndex(index);
  const saveMarker = () => {
    const markerDetails: Partial<EditMarkerProps> = {
      name: markerName,
      description: markerDescription,
      notes: markerNotes,
      isPrivate: isPrivateMarker,
      expires: markerExpires,
      showDate: markerExpires ? showDate : undefined,
      hideDate: markerExpires ? hideDate : undefined,
    };
    if (!markerName) {
      setMarkerNameValid(false);
      setMarkerNameValidation("Invalid marker name.");
      return;
    }
    if (onSave) onSave(markerDetails as EditMarkerProps);
  };
  return (
    <>
      <Box width={sm ? 300 : 450} className={classes.root}>
        <Paper elevation={16}>
          <Box p={8} pb={4} pt={4} className={classes.header} position="relative">
            <Typography variant="h6" className={classes.subtitle}>
              Map Marker
            </Typography>
            <Typography variant="h4">New Marker</Typography>
            <IconButton className={classes.close} onClick={() => onClose && onClose()}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box p={8} pt={0} className={classes.content}>
            <TextField
              id="name"
              label=""
              variant="outlined"
              placeholder="Marker name"
              value={markerName}
              margin="normal"
              fullWidth
              onChange={(e) => {
                setMarkerName(e.target.value);
              }}
              error={!markerNameValid}
              helperText={markerNameValidation}
            />
            <TextField
              id="description"
              label=""
              variant="outlined"
              placeholder="Description"
              value={markerDescription}
              margin="normal"
              fullWidth
              onChange={(e) => {
                setMarkerDescription(e.target.value);
              }}
            />
            <TextField
              id="notes"
              label=""
              variant="outlined"
              placeholder="Notes"
              value={markerNotes}
              margin="normal"
              fullWidth
              onChange={(e) => {
                setMarkerNotes(e.target.value);
              }}
            />
            <Typography variant="h6" className={clsx(classes.subtitle, classes.mt)}>
              Appearance
            </Typography>
            <Box display="flex" flexWrap="wrap" width={0.75}>
              <ToggleButtonGroup
                onChange={onSelectMarker}
                size="medium"
                exclusive
                classes={{ root: classes.markerGroup, grouped: classes.markerButton }}
              >
                {markerColours.map((markColour) => (
                  <ToggleButton key={markColour} value={markColour} selected={selectedIndex === markColour}>
                    <Marker fill={markColour} />
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Typography variant="h6" className={clsx(classes.subtitle, classes.mt)}>
              Visibility
            </Typography>
            <FormGroup>
              <FormControlLabel
                classes={{ label: classes.switchLabel }}
                control={
                  <Switch
                    checked={isPrivateMarker}
                    color="primary"
                    onChange={(_e, checked) => setIsPrivateMarker(checked)}
                    name="isPrivate"
                  />
                }
                label="Private marker"
                labelPlacement="start"
              />
              <FormControlLabel
                classes={{ label: classes.switchLabel }}
                control={
                  <Switch
                    checked={markerExpires}
                    color="primary"
                    onChange={(_e, checked) => setMarkerExpires(checked)}
                    name="markerExpires"
                  />
                }
                label="Marker expires"
                labelPlacement="start"
              />
            </FormGroup>
            {markerExpires && (
              <>
                <TextField
                  id="showdate"
                  label="Show"
                  type="date"
                  variant="outlined"
                  defaultValue={new Date().toString()}
                  value={fromDate}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  fullWidth
                />
                <TextField
                  id="hidedate"
                  label="Hide"
                  type="date"
                  variant="outlined"
                  defaultValue={new Date().toString()}
                  value={toDate}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  fullWidth
                />
              </>
            )}
          </Box>
          <Box p={8} pt={4} pb={4} className={classes.footer}>
            <Button variant="contained" color="primary" onClick={() => saveMarker()}>
              Save
            </Button>
            <Button color="primary" onClick={() => onClose && onClose()}>
              Cancel
            </Button>
          </Box>
        </Paper>
      </Box>
    </>
  );
};
export default EditMarker;
